import { useState, useEffect } from "react";
import { Plus, Loader2, Copy, KeyRound, Trash2, Pencil, ExternalLink, Eye, EyeOff, CalendarClock, Lock, Unlock, RefreshCw, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { ConfirmDialog, PasswordDialog } from "@/components/Dialogs";

const storeUrl = (slug) => `${window.location.origin}/toko/${slug}`;
// BUG 8: URL lengkap & dapat diklik. Hanya memakai route yang sudah ada di App.js (/toko/:slug/admin dan /superadmin).
const adminUrl = (slug) => `${window.location.origin}/toko/${slug}/admin`;
const superadminUrl = () => `${window.location.origin}/superadmin`;
const EMPTY = { username: "", password: "", slug: "", store_name: "", whatsapp_number: "", copy_from_store_id: "", billing_plan: "", feature_preset: "pro" };

const fmtDate = (iso) => { if (!iso) return "-"; try { return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };
const daysLeft = (iso) => { if (!iso) return null; const d = Math.ceil((new Date(iso + "T00:00:00") - new Date(new Date().toDateString())) / 86400000); return d; };

// Ringkasan & aksi langganan per toko (khusus Superadmin)
export function SubscriptionPanel({ store, reload }) {
  const [busy, setBusy] = useState("");
  const s = store || {};
  const dl = daysLeft(s.due_date);
  const suspended = s.subscription_status === "suspended";
  const isTrial = s.subscription_status === "trial";
  const trialMs = s.trial_due_at ? new Date(s.trial_due_at) - new Date() : null;
  const trialHoursLeft = trialMs != null ? Math.floor(trialMs / 3600000) : null;
  const trialExpired = isTrial && trialMs != null && trialMs <= 0;

  let badge = { text: "Belum Berlangganan", color: "#64748b", bg: "#f1f5f9" };
  if (suspended) badge = { text: "🔴 Terkunci", color: "#b91c1c", bg: "#fee2e2" };
  else if (trialExpired) badge = { text: "🔴 Trial Berakhir", color: "#b91c1c", bg: "#fee2e2" };
  else if (isTrial) badge = { text: `🟣 Trial Aktif (sisa ${Math.floor((trialHoursLeft || 0) / 24)} hari ${(trialHoursLeft || 0) % 24} jam)`, color: "#6d28d9", bg: "#ede9fe" };
  else if (dl != null && dl < 0) badge = { text: "🔴 Lewat Jatuh Tempo", color: "#b91c1c", bg: "#fee2e2" };
  else if (dl != null && dl <= 5) badge = { text: `🟡 H-${dl} Jatuh Tempo`, color: "#92400e", bg: "#fef3c7" };
  else if (dl != null) badge = { text: "🟢 Aktif", color: "#166534", bg: "#dcfce7" };

  const act = async (action, plan) => {
    setBusy(action);
    try { await api.put(`/admin/stores/${s.id}/subscription`, { action, plan }); toast.success("Langganan diperbarui"); await reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const setTier = async (tier) => {
    setBusy("tier-" + tier);
    try { await api.put(`/admin/stores/${s.id}/plan-tier`, { plan_tier: tier }); toast.success(`Paket toko: ${tier === "pro" ? "PRO" : "STANDAR"}`); await reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className="sub-panel" data-testid={`sub-panel-${s.slug}`} style={{ marginTop: 8, borderTop: "1px dashed #e2e8f0", paddingTop: 8 }}>
      <div className="field-label" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><CalendarClock size={13} /> Langganan (khusus Superadmin)</div>
      <div className="muted-sm" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <span style={{ background: badge.bg, color: badge.color, fontWeight: 700, padding: "2px 8px", borderRadius: 999, fontSize: 12 }} data-testid={`sub-status-${s.slug}`}>{badge.text}</span>
        <span>Paket: <strong>{s.billing_plan === "yearly" ? "Tahunan" : s.billing_plan === "monthly" ? "Bulanan" : isTrial ? "Trial" : "-"}</strong></span>
        {isTrial
          ? <span>Trial berakhir: <strong data-testid={`sub-trial-due-${s.slug}`}>{new Date(s.trial_due_at).toLocaleString("id-ID")}</strong></span>
          : <span>Jatuh tempo: <strong data-testid={`sub-due-${s.slug}`}>{fmtDate(s.due_date)}</strong>{dl != null && <> ({dl < 0 ? `lewat ${-dl} hari` : `sisa ${dl} hari`})</>}</span>}
      </div>
      <div className="muted-sm" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <span>Paket Aplikasi: <strong style={{ background: s.plan_tier === "pro" ? "#dcfce7" : "#f1f5f9", color: s.plan_tier === "pro" ? "#166534" : "#475569", padding: "2px 8px", borderRadius: 999, fontSize: 12 }} data-testid={`plan-tier-${s.slug}`}>{s.plan_tier === "pro" ? "PRO" : "STANDAR"}</strong></span>
        <span>Logo: <strong data-testid={`logo-status-${s.slug}`}>{s.logo_path ? "Kustom" : "Default"}</strong></span>
        {s.plan_tier === "pro"
          ? <button className="btn-ghost sm" onClick={() => setTier("standard")} disabled={!!busy} data-testid={`tier-standard-${s.slug}`}><BadgeCheck size={13} /> Turunkan ke STANDAR</button>
          : <button className="btn-primary sm" onClick={() => setTier("pro")} disabled={!!busy} data-testid={`tier-pro-${s.slug}`}><BadgeCheck size={13} /> Upgrade ke PRO</button>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {!isTrial && !s.due_date && (
          <button className="btn-ghost sm" onClick={() => act("start_trial")} disabled={!!busy} data-testid={`sub-start-trial-${s.slug}`}><CalendarClock size={13} /> Mulai Trial 5 Hari</button>
        )}
        <button className="btn-ghost sm" onClick={() => act("renew_monthly")} disabled={!!busy} data-testid={`sub-renew-monthly-${s.slug}`}>{busy === "renew_monthly" ? <Loader2 className="spin" size={13} /> : <Plus size={13} />} Perpanjang Bulanan (+1 bln)</button>
        <button className="btn-ghost sm" onClick={() => act("renew_yearly")} disabled={!!busy} data-testid={`sub-renew-yearly-${s.slug}`}>{busy === "renew_yearly" ? <Loader2 className="spin" size={13} /> : <Plus size={13} />} Perpanjang Tahunan (+1 thn)</button>
        {!s.due_date && (
          <button className="btn-primary sm" onClick={() => act("activate", "monthly")} disabled={!!busy} data-testid={`sub-activate-${s.slug}`}><RefreshCw size={13} /> Tandai Lunas / Aktifkan</button>
        )}
        {suspended
          ? <button className="btn-ghost sm" onClick={() => act("unlock")} disabled={!!busy} data-testid={`sub-unlock-${s.slug}`}><Unlock size={13} /> Buka Kunci</button>
          : <button className="btn-ghost sm danger-text" onClick={() => act("lock")} disabled={!!busy} data-testid={`sub-lock-${s.slug}`}><Lock size={13} /> Kunci Toko</button>}
      </div>
    </div>
  );
}

// Daftar hak akses fitur per toko (dikelola superadmin)
export const ACCESS_RIGHTS = [
  { key: "hapus", label: "Hapus" },
  { key: "edit", label: "Edit" },
  { key: "hapus_semua", label: "Hapus Semua" },
  { key: "kirim_semua_belum", label: "Kirim Semua yang Belum" },
  { key: "import_ebiling", label: "Import Ebiling" },
  { key: "tambah_reseller", label: "Tambah Reseller (Menu Reseller)" },
  { key: "tambah_voucher", label: "Tambah Voucher" },
  { key: "tambah_item", label: "Tambah Item" },
  { key: "rentang_waktu_laporan", label: "Rentang Waktu Laporan (Pesanan & Laporan)" },
  { key: "print_export_laporan", label: "Print & Export Laporan (Excel)" },
];

// Preset paket fitur (khusus Superadmin)
const ALL_ON = Object.fromEntries(ACCESS_RIGHTS.map((r) => [r.key, true]));
const DEFAULT_RIGHTS_MAP = { ...ALL_ON };
const RIGHT_PRESETS = {
  basic: { ...ALL_ON, hapus_semua: false, import_ebiling: false, tambah_voucher: false, rentang_waktu_laporan: false, print_export_laporan: false },
  pro: ALL_ON,
};

const STATUS_META = {
  active: { label: "AKTIF", color: "#166534", bg: "#dcfce7" },
  locked: { label: "TERKUNCI", color: "#b91c1c", bg: "#fee2e2" },
  pro: { label: "PRO", color: "#92400e", bg: "#fef3c7" },
  coming_soon: { label: "SEGERA HADIR", color: "#1e40af", bg: "#dbeafe" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.active;
  return <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 999, background: m.bg, color: m.color, letterSpacing: 0.3 }} data-testid={`feat-status-${status}`}>{m.label}</span>;
}

// Hak Akses berkategori dari Feature Registry (metadata) — enforcement tetap via access_rights/check_right backend.
function AccessRightsPanel({ store }) {
  const [reg, setReg] = useState(null);
  const [savingKey, setSavingKey] = useState("");

  const loadReg = async () => {
    try { const r = await api.get("/admin/feature-registry", { params: { store_id: store.id } }); setReg(r.data); }
    catch (e) { toast.error(errMsg(e, "Gagal memuat daftar fitur")); }
  };
  useEffect(() => { loadReg(); }, [store.id, store.access_rights, store.plan_tier]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRight = async (accessKey, nextActive) => {
    const base = { ...DEFAULT_RIGHTS_MAP, ...(store.access_rights || {}) };
    const next = { ...base, [accessKey]: nextActive };
    setSavingKey(accessKey);
    try {
      await api.put(`/admin/stores/${store.id}/access-rights`, { access_rights: next });
      toast.success("Hak Akses Toko Disimpan");
      store.access_rights = next; // sinkron lokal agar refetch status akurat
      await loadReg();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingKey(""); }
  };

  const applyPreset = async (preset) => {
    setSavingKey(preset);
    const next = { ...RIGHT_PRESETS[preset] };
    try {
      await api.put(`/admin/stores/${store.id}/access-rights`, { access_rights: next });
      toast.success(`Paket "${preset === "pro" ? "Pro (Lengkap)" : "Basic"}" diterapkan`);
      store.access_rights = next;
      await loadReg();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingKey(""); }
  };

  return (
    <div className="rights-panel" data-testid={`rights-panel-${store.slug}`} style={{ marginTop: 8, borderTop: "1px dashed #e2e8f0", paddingTop: 8 }}>
      <div className="field-label" style={{ marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span>Hak Akses Fitur — per kategori (khusus Superadmin)</span>
        <span style={{ display: "flex", gap: 4 }}>
          <button className="btn-ghost sm" onClick={() => applyPreset("basic")} disabled={!!savingKey} data-testid={`preset-basic-${store.slug}`} style={{ fontSize: 11, padding: "3px 8px" }}>Paket Basic</button>
          <button className="btn-ghost sm" onClick={() => applyPreset("pro")} disabled={!!savingKey} data-testid={`preset-pro-${store.slug}`} style={{ fontSize: 11, padding: "3px 8px" }}>Paket Pro</button>
        </span>
      </div>
      {!reg ? <p className="muted-sm">Memuat daftar fitur…</p> : (
        <div data-testid={`feature-registry-${store.slug}`}>
          {reg.categories.map((cat) => (
            <div key={cat.category} style={{ marginBottom: 8 }} data-testid={`feat-cat-${cat.category}`}>
              <div style={{ fontWeight: 800, fontSize: 12, color: "#334155", margin: "6px 0 4px", textTransform: "uppercase", letterSpacing: 0.4 }}>Hak Akses {cat.category}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 6 }}>
                {cat.features.map((f) => {
                  const toggleable = f.kind === "right";
                  const checked = f.status === "active";
                  return (
                    <label key={f.key} className="muted-sm" style={{ display: "flex", alignItems: "center", gap: 6, opacity: toggleable ? 1 : 0.85, cursor: toggleable ? (savingKey === f.access_key ? "wait" : "pointer") : "default" }} data-testid={`feat-${f.key}`}>
                      {toggleable ? (
                        <input type="checkbox" checked={checked} disabled={savingKey === f.access_key}
                          onChange={(e) => toggleRight(f.access_key, e.target.checked)} data-testid={`feat-toggle-${f.key}`} />
                      ) : f.kind === "plan" ? <Lock size={12} style={{ opacity: 0.6 }} /> : <span style={{ width: 12 }} />}
                      <span style={{ flex: 1 }}>{f.name}</span>
                      <StatusBadge status={f.status} />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 8 }} data-testid={`integration-providers-${store.slug}`}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#334155", margin: "6px 0 4px", textTransform: "uppercase", letterSpacing: 0.4 }}>Provider Integrasi</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {reg.providers.map((p) => (
                <span key={p.key} className="muted-sm" style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #e2e8f0", borderRadius: 8, padding: "3px 8px" }} data-testid={`provider-${p.key}`}>
                  {p.name} <StatusBadge status={p.status} />
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function StoreManager({ stores, reload, onManage, mainSlug }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [showPw, setShowPw] = useState({});

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const mainStore = stores.find((s) => s.store.slug === mainSlug)?.store;
  const srcStore = stores.find((s) => s.store.id === (form.copy_from_store_id || mainStore?.id))?.store;

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/admin/stores", { ...form, copy_from_store_id: form.copy_from_store_id || mainStore?.id || null });
      if (form.feature_preset && RIGHT_PRESETS[form.feature_preset]) {
        await api.put(`/admin/stores/${res.data.store.id}/access-rights`, { access_rights: { ...RIGHT_PRESETS[form.feature_preset] } });
      }
      toast.success(`Toko "${form.store_name}" dibuat. Link: ${storeUrl(form.slug.toLowerCase())}`);
      setForm(EMPTY); setOpen(false);
      await reload();
    } catch (err) { toast.error(errMsg(err, "Gagal membuat toko")); }
    finally { setBusy(false); }
  };

  const copy = (slug) => { navigator.clipboard.writeText(storeUrl(slug)); toast.success("Link toko disalin"); };

  const resetPw = async (pw) => {
    try { await api.put(`/admin/stores/${pwTarget.store.id}/password`, { password: pw }); toast.success(`Password admin "${pwTarget.admin?.username}" diganti`); await reload(); }
    catch (err) { throw new Error(errMsg(err)); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/stores/${delTarget.store.id}`); toast.success("Toko dihapus"); await reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="store-manager">
      <div className="store-grid">
        {stores.map((s) => {
          const adminPw = s.admin?.password_plain || "";
          const visible = !!showPw[s.store.id];
          return (
            <div className="store-card" key={s.store.id} data-testid={`store-card-${s.store.slug}`}>
              <div>
                <strong data-testid={`store-card-name-${s.store.slug}`}>{s.store.store_name}</strong>
                {s.store.slug === mainSlug && <span className="badge">Utama</span>}
                <div className="muted-sm">Admin: {s.admin?.username || "-"} · {s.voucher_count} voucher · {s.order_count} pesanan</div>
                {/* Password admin terlihat & bisa diedit — khusus superadmin */}
                <div className="muted-sm" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <span>Password:</span>
                  <code style={{ fontWeight: 700, letterSpacing: 0.5 }} data-testid={`store-admin-password-${s.store.slug}`}>
                    {visible ? adminPw || "(tidak tampil)" : "••••••••"}
                  </code>
                  <button className="icon-btn" onClick={() => setShowPw({ ...showPw, [s.store.id]: !visible })} title={visible ? "Sembunyikan" : "Lihat password"} data-testid={`toggle-admin-password-${s.store.slug}`}>
                    {visible ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
              </div>
              <div className="store-link">
                <a href={storeUrl(s.store.slug)} target="_blank" rel="noreferrer" data-testid={`store-link-${s.store.slug}`}>
                  /toko/{s.store.slug} <ExternalLink size={12} />
                </a>
                <button className="icon-btn" onClick={() => copy(s.store.slug)} title="Salin link" data-testid={`store-copy-${s.store.slug}`}><Copy size={15} /></button>
              </div>
              <div className="store-link" data-testid={`store-admin-link-row-${s.store.slug}`}>
                <span className="muted-sm">Login Admin:</span>{" "}
                <a href={adminUrl(s.store.slug)} target="_blank" rel="noreferrer" data-testid={`store-admin-link-${s.store.slug}`}>
                  {adminUrl(s.store.slug)} <ExternalLink size={12} />
                </a>
                <button className="icon-btn" onClick={() => { navigator.clipboard.writeText(adminUrl(s.store.slug)); toast.success("Link login Admin disalin"); }} title="Salin link login Admin" data-testid={`store-admin-copy-${s.store.slug}`}><Copy size={15} /></button>
              </div>
              <div className="store-link" data-testid={`store-superadmin-link-row-${s.store.slug}`}>
                <span className="muted-sm">Login Superadmin:</span>{" "}
                <a href={superadminUrl()} target="_blank" rel="noreferrer" data-testid={`store-superadmin-link-${s.store.slug}`}>
                  {superadminUrl()} <ExternalLink size={12} />
                </a>
                <button className="icon-btn" onClick={() => { navigator.clipboard.writeText(superadminUrl()); toast.success("Link login Superadmin disalin"); }} title="Salin link login Superadmin" data-testid={`store-superadmin-copy-${s.store.slug}`}><Copy size={15} /></button>
              </div>
              <div className="store-actions">
                <button className="btn-ghost sm" onClick={() => onManage(s.store)} data-testid={`store-manage-${s.store.slug}`}><Pencil size={14} /> Kelola</button>
                <button className="btn-ghost sm" onClick={() => setPwTarget(s)} data-testid={`store-resetpw-${s.store.slug}`}><KeyRound size={14} /> Password</button>
                {s.store.slug !== mainSlug && (
                  <button className="btn-ghost sm danger-text" onClick={() => setDelTarget(s)} data-testid={`store-delete-${s.store.slug}`}><Trash2 size={14} /> Hapus</button>
                )}
              </div>
              <SubscriptionPanel store={s.store} reload={reload} />
              <AccessRightsPanel store={s.store} />
            </div>
          );
        })}
      </div>

      {!open ? (
        <button className="add-item-btn" onClick={() => setOpen(true)} data-testid="open-create-store"><Plus size={16} /> Tambah Admin / Toko Baru</button>
      ) : (
        <form className="create-store-form" onSubmit={create} data-testid="create-store-form">
          <h3 className="section-title">Admin / Toko Baru</h3>
          <div className="form-grid">
            <div>
              <label className="field-label">Nama Toko</label>
              <input className="text-input" value={form.store_name} onChange={set("store_name")} required data-testid="create-store-name" />
            </div>
            <div>
              <label className="field-label">Link Toko (huruf kecil, tanpa spasi)</label>
              <input className="text-input" value={form.slug} onChange={set("slug")} placeholder="mis. budi" required data-testid="create-store-slug" />
            </div>
            <div>
              <label className="field-label">Username Admin</label>
              <input className="text-input" value={form.username} onChange={set("username")} required data-testid="create-store-username" />
            </div>
            <div>
              <label className="field-label">Password Admin</label>
              <input className="text-input" type="text" value={form.password} onChange={set("password")} required minLength={6} data-testid="create-store-password" />
            </div>
            <div>
              <label className="field-label">Nomor WhatsApp Toko</label>
              <input className="text-input" value={form.whatsapp_number} onChange={set("whatsapp_number")} placeholder={srcStore?.whatsapp_number ? `kosongkan = pakai ${srcStore.whatsapp_number}` : "6281234567890"} data-testid="create-store-whatsapp" />
            </div>
            <div>
              <label className="field-label">Salin voucher, harga &amp; info dari toko</label>
              <select className="text-input" value={form.copy_from_store_id || mainStore?.id || ""} onChange={set("copy_from_store_id")} data-testid="create-store-copy-from">
                {stores.map((s) => <option key={s.store.id} value={s.store.id}>{`${s.store.store_name} (${s.voucher_count} voucher)${s.store.slug === mainSlug ? " — Utama" : ""}`}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Paket Langganan</label>
              <select className="text-input" value={form.billing_plan} onChange={set("billing_plan")} data-testid="create-store-plan">
                <option value="">Trial 5 Hari dulu (otomatis)</option>
                <option value="monthly">Bulanan (+1 bulan)</option>
                <option value="yearly">Tahunan — hemat 2 bulan (+1 tahun)</option>
              </select>
            </div>
            <div>
              <label className="field-label">Paket Fitur Standar</label>
              <select className="text-input" value={form.feature_preset} onChange={set("feature_preset")} data-testid="create-store-preset">
                <option value="pro">Pro — Semua fitur aktif</option>
                <option value="basic">Basic — Tanpa import ebiling, rentang waktu, export/print, tambah voucher, hapus semua</option>
              </select>
            </div>
          </div>
          <p className="muted-sm" data-testid="create-store-copy-note">Toko baru akan langsung berisi {srcStore?.store_name ? `daftar voucher & harga terbaru dari "${srcStore.store_name}"` : "voucher dari toko sumber"}; subjudul & nomor WA ikut disalin jika dikosongkan.</p>
          {form.slug && <p className="muted-sm">Link pelanggan: {storeUrl(form.slug.toLowerCase())}</p>}
          <div className="actions-right">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)} data-testid="create-store-cancel">Batal</button>
            <button type="submit" className="btn-primary" disabled={busy} data-testid="create-store-submit">
              {busy ? <Loader2 className="spin" size={16} /> : <Plus size={16} />} Buat Toko
            </button>
          </div>
        </form>
      )}

      <PasswordDialog
        open={!!pwTarget}
        onClose={() => setPwTarget(null)}
        title="Reset Password Admin"
        description={`Password baru untuk admin "${pwTarget?.admin?.username}" (toko ${pwTarget?.store.store_name}).`}
        onSubmit={resetPw}
        testId="store-pw-dialog"
      />
      <ConfirmDialog
        open={!!delTarget}
        onClose={() => setDelTarget(null)}
        title="Hapus Toko"
        message={`Toko "${delTarget?.store.store_name}" beserta admin, voucher, dan semua pesanannya akan dihapus permanen. Lanjutkan?`}
        confirmLabel="Hapus Toko"
        onConfirm={remove}
        testId="store-del-dialog"
      />
    </div>
  );
}
